// lib/studio-runtime-dto.ts
import { isStudioProjectType } from '#lib/studio-project-view';
import {
  normalizeLayerWorkspaceId,
  type CanvasResizeState,
  type FrameResizeState,
  type Layer,
  type ProjectType,
  type StudioLayer,
  type StudioLayerDTO,
  type StudioLayerRuntimeState,
  type StudioTimelineComposedPlaybackRuntime,
  type StudioWorkspace,
  type StudioWorkspaceDTO,
  type StudioWorkspaceRuntimeState,
  type StudioWorkspaceVersionDTO,
  type StudioWorkspaceVersionStatus,
  type Workspace,
  type WorkspaceVersion,
} from '#types/types';

export type StudioLayerAutosavePayload = Omit<StudioLayerDTO, 'id' | 'createdAt' | 'updatedAt'>;
export type StudioLayerMutationPayload = Omit<StudioLayerAutosavePayload, 'workspaceId'> & {
  workspaceId: string | null;
};
export type StudioWorkspaceAutosavePayload = Omit<StudioWorkspaceDTO, 'id' | 'createdAt' | 'updatedAt' | 'backgroundColor' | 'layerIds' | 'versionIds'>;
export type StudioProjectAutosavePayload = Partial<{
  projectType: ProjectType;
}>;
export type StudioWorkspaceVersionAutosavePayload = {
  upserts: Array<{
    id: string;
    action: 'CREATE' | 'UPDATE';
    payload: Omit<StudioWorkspaceVersionDTO, 'id' | 'createdAt' | 'updatedAt'>;
    annotationMaskIsDirty: boolean;
  }>;
  detachIds: string[];
};

const VIDEO_URL_EXTENSION_PATTERN = /\.(mp4|mov|webm|m4v|mkv)(?:[?#]|$)/i;

const sanitizeWorkspaceVersionImageUrl = (url: string | null | undefined): string | null => {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed || VIDEO_URL_EXTENSION_PATTERN.test(trimmed)) return null;
  return trimmed;
};

export const DEFAULT_CANVAS_RESIZE_STATE: CanvasResizeState = {
  isResizing: false,
  handleType: null,
  startPos: null,
  initialRect: null,
  delta: { dx: 0, dy: 0 },
  isShiftPressed: false,
  workspaceId: null,
};

export const DEFAULT_FRAME_RESIZE_STATE: FrameResizeState = {
  isResizing: false,
  handleType: null,
  startPos: null,
  initialRect: null,
  delta: { dx: 0, dy: 0 },
  workspaceId: null,
};

/**
 * Deep-copies the mutable parts of a Layer (rect, params, modificationLog) so an undo/redo
 * snapshot never shares a reference with the live registry record.
 */
export const cloneLayerSnapshot = (layer: Layer): Layer => ({
  ...layer,
  rect: { ...layer.rect },
  params:
    layer.params && typeof layer.params === 'object'
      ? ({ ...(layer.params as Record<string, unknown>) } as Layer['params'])
      : layer.params,
  modificationLog: layer.modificationLog
    ? layer.modificationLog.map((entry) => ({ ...entry }))
    : undefined,
});

const toNormalizedStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    : [];

const toNullableFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toComposedPlaybackRuntime = (
  value: unknown
): StudioTimelineComposedPlaybackRuntime | null => {
  if (!isRecord(value)) return null;
  const sourceUrl = typeof value.sourceUrl === 'string' && value.sourceUrl.length > 0
    ? value.sourceUrl
    : null;
  const preparePercent =
    typeof value.preparePercent === 'number' && Number.isFinite(value.preparePercent)
      ? value.preparePercent
      : 0;
  return {
    sourceUrl,
    isPreparing: value.isPreparing === true,
    preparePercent,
    prepareMessage: typeof value.prepareMessage === 'string' ? value.prepareMessage : null,
  };
};

export const toWorkspaceRuntimeOverlay = (
  workspace: Workspace
): Required<StudioWorkspaceRuntimeState> => ({
  activeVersionId:
    typeof workspace.activeVersionId === 'string'
      ? workspace.activeVersionId
      : null,
  annotationMask: workspace.annotationMask ?? null,
  annotationMaskVersionId: workspace.annotationMaskVersionId ?? null,
  versionFilterMode: workspace.versionFilterMode === 'ALL' ? 'ALL' : 'PRIMARY',
  isResizingCanvas: workspace.isResizingCanvas === true,
  canvasResizeState: workspace.canvasResizeState ?? DEFAULT_CANVAS_RESIZE_STATE,
  frameResizeState: workspace.frameResizeState ?? DEFAULT_FRAME_RESIZE_STATE,
  expanded: workspace.expanded !== false,
  locked: workspace.locked === true,
  versions: Array.isArray(workspace.versions) ? workspace.versions : [],
  animatedGenerationRect: workspace.animatedGenerationRect ?? null,
  preferVersionPanelOverTimeline: workspace.preferVersionPanelOverTimeline === true,
  imageTimelineManuallyEnabled: workspace.imageTimelineManuallyEnabled === true,
  timelineCursorMs: toNullableFiniteNumber(workspace.timelineCursorMs),
  isTimelinePlaying: workspace.isTimelinePlaying === true,
  timelinePlaybackRate:
    typeof workspace.timelinePlaybackRate === 'number' &&
    Number.isFinite(workspace.timelinePlaybackRate) &&
    workspace.timelinePlaybackRate > 0
      ? workspace.timelinePlaybackRate
      : 1,
  isTimelineMuted: workspace.isTimelineMuted === true,
  isTimelineLoopEnabled: workspace.isTimelineLoopEnabled === true,
  timelineComposedPlayback: toComposedPlaybackRuntime(workspace.timelineComposedPlayback),
  isImportingAssets: workspace.isImportingAssets === true,
  isMountingLayers: workspace.isMountingLayers === true,
});

export const toLayerRuntimeOverlay = (layer: Layer): Partial<StudioLayerRuntimeState> => ({
  imageData: layer.imageData ?? null,
  videoBlob: layer.videoBlob ?? null,
  isAltered: layer.isAltered === true,
  skipRender: layer.skipRender === true ? true : undefined,
  locked: layer.locked === true,
  opacity: typeof layer.opacity === 'number' && Number.isFinite(layer.opacity) ? layer.opacity : 1,
  mustRender: layer.mustRender === true ? true : undefined,
  isPlaying: layer.isPlaying === true ? true : undefined,
  loadError: layer.loadError === true ? true : undefined,
  pulseToken:
    typeof layer.pulseToken === 'number' && Number.isFinite(layer.pulseToken)
      ? layer.pulseToken
      : undefined,
  isVectorizing: layer.isVectorizing === true ? true : undefined,
  isRasterizing: layer.isRasterizing === true ? true : undefined,
  isAligning: layer.isAligning === true ? true : undefined,
  isExtracting: layer.isExtracting === true ? true : undefined,
  isHarmonizingUnchanged: layer.isHarmonizingUnchanged === true ? true : undefined,
  isIntegrating: layer.isIntegrating === true ? true : undefined,
});

const isLayerParams = (value: unknown): value is NonNullable<Layer['params']> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readStringField = (value: unknown, key: string): string => {
  if (!value || typeof value !== 'object') return '';
  const candidate = Reflect.get(value, key);
  return typeof candidate === 'string' ? candidate.trim() : '';
};

const readNumberField = (value: unknown, key: string): number | null => {
  if (!value || typeof value !== 'object') return null;
  const candidate = Reflect.get(value, key);
  if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;
  if (typeof candidate === 'string' && candidate.trim().length > 0) {
    const parsed = Number(candidate);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const readBooleanField = (value: unknown, key: string): boolean | null => {
  if (!value || typeof value !== 'object') return null;
  const candidate = Reflect.get(value, key);
  return typeof candidate === 'boolean' ? candidate : null;
};

const resolveLayerWorkspaceIdFromEntity = (value: unknown): string | null => {
  if (isRecord(value) && Object.prototype.hasOwnProperty.call(value, 'layerWorkspaceId')) {
    return normalizeLayerWorkspaceId(Reflect.get(value, 'layerWorkspaceId'));
  }
  return isRecord(value)
    ? normalizeLayerWorkspaceId(Reflect.get(value, 'workspaceId'))
    : null;
};

export const toWorkspaceState = (workspace: StudioWorkspace): Workspace => ({
  ...workspace,
  activeVersionId:
    typeof workspace.activeVersionId === 'string'
      ? workspace.activeVersionId
      : null,
  lastMaskUrl: workspace.lastMaskUrl ?? undefined,
  annotationMask: workspace.annotationMask ?? null,
  annotationMaskVersionId:
    typeof workspace.annotationMaskVersionId === 'string'
      ? workspace.annotationMaskVersionId
      : null,
  annotationsEnabled: workspace.annotationsEnabled !== false,
  annotationOpaque: workspace.annotationOpaque === true,
  isResizingCanvas: workspace.isResizingCanvas === true,
  canvasResizeState: workspace.canvasResizeState ?? DEFAULT_CANVAS_RESIZE_STATE,
  frameResizeState: workspace.frameResizeState ?? DEFAULT_FRAME_RESIZE_STATE,
  expanded: workspace.expanded !== false,
  locked: workspace.locked === true,
  visible: workspace.visible !== false,
  cameraState: workspace.cameraState ?? undefined,
  timelineDurationMs: toNullableFiniteNumber(workspace.timelineDurationMs),
  timelineFps: toNullableFiniteNumber(workspace.timelineFps),
  versions: Array.isArray(workspace.versions) ? workspace.versions : [],
  versionFilterMode: workspace.versionFilterMode === 'ALL' ? 'ALL' : 'PRIMARY',
  preferVersionPanelOverTimeline: workspace.preferVersionPanelOverTimeline === true,
  imageTimelineManuallyEnabled: workspace.imageTimelineManuallyEnabled === true,
  timelineCursorMs: toNullableFiniteNumber(workspace.timelineCursorMs),
  isImportingAssets: workspace.isImportingAssets === true,
  isMountingLayers: workspace.isMountingLayers === true,
});

export const toLayerState = (layer: StudioLayer): Layer => {
  const workspaceId = resolveLayerWorkspaceIdFromEntity(layer);
  const source = typeof layer.source === 'string' ? layer.source : '';
  const timestamp = readNumberField(layer, 'timestamp');
  const contentVersion = readNumberField(layer, 'contentVersion');
  const timelineStartMs = readNumberField(layer, 'timelineStartMs');
  const timelineEndMs = readNumberField(layer, 'timelineEndMs');
  const timelineSourceInMs = readNumberField(layer, 'timelineSourceInMs');
  const topLayerLegacy = readBooleanField(layer, 'top-layer') === true;
  const params = isLayerParams(layer.params) ? layer.params : undefined;
  const layerType =
    layer.type === 'video' || layer.type === 'audio' || layer.type === 'svg'
      ? layer.type
      : undefined;
  const opacity = readNumberField(layer, 'opacity');
  return {
    id: layer.id,
    projectId: layer.projectId,
    renderOrder: layer.renderOrder ?? undefined,
    layerWorkspaceId: workspaceId,
    'top-layer': layer.topLayer === true || topLayerLegacy,
    imageSrc: layer.imageSrc ?? undefined,
    rect: layer.rect,
    imageData: layer.imageData ?? null,
    videoBlob: layer.videoBlob ?? null,
    scale: layer.scale ?? undefined,
    visible: layer.visible !== false,
    name: layer.name,
    locked: readBooleanField(layer, 'locked') !== false,
    type: layerType,
    opacity: opacity ?? 1,
    isAltered: layer.isAltered === true,
    skipRender: layer.skipRender === true ? true : undefined,
    mustRender: layer.mustRender === true ? true : undefined,
    isPlaying: layer.isPlaying === true ? true : undefined,
    loadError: layer.loadError === true ? true : undefined,
    params,
    source,
    timestamp: timestamp ?? undefined,
    contentVersion: contentVersion ?? undefined,
    videoSrc: layer.videoSrc ?? undefined,
    videoFramesUrls: Array.isArray(layer.videoFramesUrls) ? layer.videoFramesUrls : undefined,
    timelineStartMs,
    timelineEndMs,
    timelineSourceInMs,
    pulseToken:
      typeof layer.pulseToken === 'number' && Number.isFinite(layer.pulseToken)
        ? layer.pulseToken
        : undefined,
    isVectorizing: layer.isVectorizing === true ? true : undefined,
    isRasterizing: layer.isRasterizing === true ? true : undefined,
    isAligning: layer.isAligning === true ? true : undefined,
    isExtracting: layer.isExtracting === true ? true : undefined,
    isHarmonizingUnchanged: layer.isHarmonizingUnchanged === true ? true : undefined,
    isIntegrating: layer.isIntegrating === true ? true : undefined,
    modificationLog: layer.modificationLog ?? undefined,
    reconstructedInputUrl: layer.reconstructedInputUrl ?? undefined,
  };
};

const toWorkspaceVersionIds = (
  workspace: Workspace,
  existingDto?: StudioWorkspaceDTO
): string[] => {
  const fromRuntime = Array.isArray(workspace.versions)
    ? workspace.versions
        .map((version) => (typeof version?.id === 'string' ? version.id : ''))
        .filter((id) => id.length > 0)
    : [];
  if (fromRuntime.length > 0) return Array.from(new Set(fromRuntime));
  return toNormalizedStringArray(existingDto?.versionIds);
};

const toDtoAutosavePayload = <TDto extends { id: string; createdAt: string; updatedAt: string }>(
  dto: TDto
): Omit<TDto, 'id' | 'createdAt' | 'updatedAt'> => {
  const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...payload } = dto;
  return payload;
};

export const toStudioLayerAutosavePayload = (dto: StudioLayerDTO): StudioLayerAutosavePayload =>
  toDtoAutosavePayload(dto);

// Membership is each layer's and version's own workspaceId: the server ignores layerIds and versionIds on
// a workspace UPDATE, and its strict schema refuses backgroundColor.
export const toStudioWorkspaceAutosavePayload = (
  dto: StudioWorkspaceDTO
): StudioWorkspaceAutosavePayload => {
  const { backgroundColor: _backgroundColor, layerIds: _layerIds, versionIds: _versionIds, ...payload } = toDtoAutosavePayload(dto);
  return payload;
};


export const toStudioProjectAutosavePayload = (
  value: unknown
): StudioProjectAutosavePayload | null => {
  if (!isRecord(value)) return null;

  const payload: StudioProjectAutosavePayload = {};
  const projectType = Reflect.get(value, 'projectType');

  if (isStudioProjectType(projectType)) {
    payload.projectType = projectType;
  }

  return Object.keys(payload).length > 0 ? payload : null;
};

export const areAutosavePayloadsEqual = <TPayload extends Record<string, unknown>>(
  left: TPayload | null,
  right: TPayload
): boolean => {
  if (!left) return false;
  return JSON.stringify(left) === JSON.stringify(right);
};

export const applyRuntimeOverlayUsingDtoBoundary = (
  currentDraft: unknown,
  dtoSnapshot: object,
  runtimeOverlay: Record<string, unknown>
): Record<string, unknown> => {
  const nextDraft = isRecord(currentDraft) ? { ...currentDraft } : {};
  const dtoKeys = new Set(Object.keys(dtoSnapshot));

  Object.keys(nextDraft).forEach((key) => {
    if (dtoKeys.has(key)) return;
    delete nextDraft[key];
  });

  Object.entries(runtimeOverlay).forEach(([key, value]) => {
    nextDraft[key] = value;
  });

  return nextDraft;
};

export const toStudioWorkspaceDtoFromRuntime = (
  workspace: Workspace,
  existingDto: StudioWorkspaceDTO | undefined,
  fallbackProjectId: string,
  nowIso: string
): StudioWorkspaceDTO => {
  const projectId = (existingDto?.projectId ?? fallbackProjectId).trim();
  const timelineDurationMs =
    typeof workspace.timelineDurationMs === 'number'
      ? workspace.timelineDurationMs
      : (workspace.timelineDurationMs === null
          ? null
          : (existingDto?.timelineDurationMs ?? null));
  const timelineFps =
    typeof workspace.timelineFps === 'number'
      ? workspace.timelineFps
      : (workspace.timelineFps === null
          ? null
          : (existingDto?.timelineFps ?? null));
  return {
    id: workspace.id,
    projectId,
    name:
      typeof workspace.name === 'string' && workspace.name.trim().length > 0
        ? workspace.name
        : workspace.id,
    visible: workspace.visible !== false,
    generationRect: workspace.generationRect,
    frameRect: workspace.frameRect,
    backgroundColor:
      typeof workspace.backgroundColor === 'string'
        ? workspace.backgroundColor
        : existingDto?.backgroundColor,
    aspectRatio: typeof workspace.aspectRatio === 'number' ? workspace.aspectRatio : null,
    annotationsEnabled: workspace.annotationsEnabled !== false,
    annotationOpaque: workspace.annotationOpaque === true,
    lastMaskUrl:
      typeof workspace.lastMaskUrl === 'string'
        ? workspace.lastMaskUrl
        : (existingDto?.lastMaskUrl ?? null),
    cameraState: workspace.cameraState ?? existingDto?.cameraState ?? null,
    validatedVersionId:
      typeof workspace.validatedVersionId === 'string' ? workspace.validatedVersionId : null,
    timelineDurationMs,
    timelineFps,
    layerIds: toNormalizedStringArray(workspace.layerIds),
    versionIds: toWorkspaceVersionIds(workspace, existingDto),
    createdAt: existingDto?.createdAt ?? nowIso,
    updatedAt: nowIso,
  };
};

const toRoundedTimelineMs = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : null;

/**
 * Champ-a-champ commun a toutes les ecritures StudioLayer. Les seuls champs dont la
 * coercition varie d'un appelant a l'autre (timeline*, source) sont produits ici avec la
 * convention "runtime seul" ; les entrees qui en veulent une autre la reaffirment
 * explicitement, sans reordonner les cles.
 */
const toStudioLayerSharedFields = (
  layer: Layer
): Omit<StudioLayerDTO, 'id' | 'projectId' | 'workspaceId' | 'createdAt' | 'updatedAt'> => ({
  renderOrder: typeof layer.renderOrder === 'number' ? layer.renderOrder : null,
  topLayer: layer['top-layer'] === true,
  name:
    typeof layer.name === 'string' && layer.name.trim().length > 0
      ? layer.name
      : layer.id,
  visible: layer.visible !== false,
  type: layer.type ?? null,
  rect: layer.rect,
  scale: typeof layer.scale === 'number' ? layer.scale : null,
  imageSrc: typeof layer.imageSrc === 'string' ? layer.imageSrc : null,
  videoSrc: typeof layer.videoSrc === 'string' ? layer.videoSrc : null,
  videoFramesUrls: Array.isArray(layer.videoFramesUrls) ? layer.videoFramesUrls : [],
  timelineStartMs: toRoundedTimelineMs(layer.timelineStartMs),
  timelineEndMs: toRoundedTimelineMs(layer.timelineEndMs),
  timelineSourceInMs: toRoundedTimelineMs(layer.timelineSourceInMs),
  source: typeof layer.source === 'string' ? layer.source : null,
  params: layer.params ?? null,
  timestamp: typeof layer.timestamp === 'number' ? layer.timestamp : null,
  contentVersion: typeof layer.contentVersion === 'number' ? layer.contentVersion : null,
  reconstructedInputUrl:
    typeof layer.reconstructedInputUrl === 'string' ? layer.reconstructedInputUrl : null,
  modificationLog: Array.isArray(layer.modificationLog) ? layer.modificationLog : null,
});

/**
 * Payload de mutation StudioLayer avec portée d’espace de travail explicite. `workspaceId: null`
 * est un détachement volontaire du layer vers son projet.
 */
export const buildStudioLayerMutationPayload = (
  layer: Layer,
  projectId: string,
  workspaceId: string | null
): StudioLayerMutationPayload => ({
  projectId,
  workspaceId,
  ...toStudioLayerSharedFields(layer),
});

export const toStudioLayerDtoFromRuntime = (
  layer: Layer,
  existingDto: StudioLayerDTO | undefined,
  fallbackProjectId: string,
  nowIso: string
): StudioLayerDTO => {
  const projectId = (existingDto?.projectId ?? fallbackProjectId).trim();
  const workspaceId =
    layer.layerWorkspaceId === undefined
      ? normalizeLayerWorkspaceId(existingDto?.workspaceId)
      : normalizeLayerWorkspaceId(layer.layerWorkspaceId);
  return {
    id: layer.id,
    projectId,
    workspaceId,
    ...toStudioLayerSharedFields(layer),
    // L'autosave conserve la valeur deja persistee quand le runtime ne dit rien (undefined),
    // et n'arrondit pas : seul un null explicite efface la colonne.
    timelineStartMs:
      typeof layer.timelineStartMs === 'number'
        ? layer.timelineStartMs
        : (layer.timelineStartMs === null
            ? null
            : (existingDto?.timelineStartMs ?? null)),
    timelineEndMs:
      typeof layer.timelineEndMs === 'number'
        ? layer.timelineEndMs
        : (layer.timelineEndMs === null
            ? null
            : (existingDto?.timelineEndMs ?? null)),
    timelineSourceInMs:
      typeof layer.timelineSourceInMs === 'number'
        ? layer.timelineSourceInMs
        : (layer.timelineSourceInMs === null
            ? null
            : (existingDto?.timelineSourceInMs ?? null)),
    // L'autosave normalise la source vide en null.
    source:
      typeof layer.source === 'string' && layer.source.trim().length > 0
        ? layer.source
        : null,
    createdAt: existingDto?.createdAt ?? nowIso,
    updatedAt: nowIso,
  };
};

const toStudioWorkspaceVersionStatus = (
  value: WorkspaceVersion['status'] | undefined
): StudioWorkspaceVersionStatus => {
  if (value === 'VALIDATED' || value === 'REJECTED' || value === 'ARCHIVED') {
    return value;
  }
  return 'CANDIDATE';
};

const toStudioWorkspaceVersionDtoFromRuntime = (
  version: WorkspaceVersion,
  workspaceId: string,
  existingDto: StudioWorkspaceVersionDTO | undefined,
  projectId: string,
  nowIso: string
): StudioWorkspaceVersionDTO => {
  const normalizedLayerVisibility = Array.isArray(version.layerVisibility)
    ? version.layerVisibility
        .map((entry) => {
          const layerId = typeof entry?.id === 'string' ? entry.id.trim() : '';
          if (!layerId) return null;
          return {
            id: layerId,
            visible: entry.visible !== false,
          };
        })
        .filter((entry): entry is StudioWorkspaceVersionDTO['layerVisibility'][number] => Boolean(entry))
    : (existingDto?.layerVisibility ?? []);

  const normalizedTimestamp = toNullableFiniteNumber(version.timestamp);

  return {
    id: version.id,
    projectId,
    workspaceId,
    parentVersionId:
      typeof version.parentId === 'string' && version.parentId.trim().length > 0
        ? version.parentId
        : null,
    name:
      typeof version.name === 'string' && version.name.trim().length > 0
        ? version.name
        : version.id,
    timestamp:
      normalizedTimestamp !== null
        ? normalizedTimestamp
        : (toNullableFiniteNumber(existingDto?.timestamp) ?? null),
    status: toStudioWorkspaceVersionStatus(version.status),
    layerVisibility: normalizedLayerVisibility,
    imageUrl:
      sanitizeWorkspaceVersionImageUrl(version.imageUrl) ??
      sanitizeWorkspaceVersionImageUrl(existingDto?.imageUrl),
    isRenderCacheValid:
      typeof version.isRenderCacheValid === 'boolean'
        ? version.isRenderCacheValid
        : (existingDto?.isRenderCacheValid ?? false),
    renderStateHash:
      typeof version.renderStateHash === 'string'
        ? version.renderStateHash
        : (existingDto?.renderStateHash ?? null),
    rejectionReason:
      typeof version.rejectionReason === 'string'
        ? version.rejectionReason
        : (existingDto?.rejectionReason ?? null),
    favorite:
      typeof version.favorite === 'boolean'
        ? version.favorite
        : (existingDto?.favorite ?? false),
    annotationComments: Array.isArray(version.annotationComments)
      ? version.annotationComments
      : (existingDto?.annotationComments ?? null),
    lastAnnotationMaskUrl:
      typeof version.lastAnnotationMaskUrl === 'string'
        ? version.lastAnnotationMaskUrl
        : version.lastAnnotationMaskUrl === null
          ? null
        : version.annotationMaskIsDirty === true && version.annotationMask === null
          ? null
          : (existingDto?.lastAnnotationMaskUrl ?? null),
    createdAt: existingDto?.createdAt ?? nowIso,
    updatedAt: nowIso,
  };
};

/** Folds an unsent version delta into the next one: a CREATE stays a CREATE, later fields win. */
export const mergeWorkspaceVersionAutosavePayloads = (
  pending: StudioWorkspaceVersionAutosavePayload,
  next: StudioWorkspaceVersionAutosavePayload,
): StudioWorkspaceVersionAutosavePayload => {
  const upserts = new Map(pending.upserts.map((upsert) => [upsert.id, upsert]));
  next.upserts.forEach((upsert) => {
    const earlier = upserts.get(upsert.id);
    upserts.set(upsert.id, earlier ? {
      ...upsert,
      action: earlier.action === 'CREATE' ? 'CREATE' : upsert.action,
      payload: { ...earlier.payload, ...upsert.payload },
      annotationMaskIsDirty: earlier.annotationMaskIsDirty || upsert.annotationMaskIsDirty,
    } : upsert);
  });
  const detachIds = new Set([...pending.detachIds, ...next.detachIds]);
  next.upserts.forEach((upsert) => detachIds.delete(upsert.id));
  return { upserts: [...upserts.values()], detachIds: [...detachIds] };
};

export const buildWorkspaceVersionAutosavePayload = (options: {
  workspaceId: string;
  projectId: string;
  previousWorkspace: Workspace | null;
  nextWorkspace: Workspace;
  versionRegistry: Record<string, StudioWorkspaceVersionDTO>;
}): StudioWorkspaceVersionAutosavePayload | null => {
  const { workspaceId, projectId, previousWorkspace, nextWorkspace, versionRegistry } = options;
  const nowIso = new Date().toISOString();

  const nextVersions = Array.isArray(nextWorkspace.versions)
    ? nextWorkspace.versions
    : [];
  const previousVersions = Array.isArray(previousWorkspace?.versions)
    ? previousWorkspace.versions
    : [];

  const previousVersionById = new Map<string, WorkspaceVersion>();
  previousVersions.forEach((version) => {
    if (!version || typeof version.id !== 'string' || version.id.trim().length === 0) return;
    previousVersionById.set(version.id, version);
  });

  const nextVersionIds = new Set<string>();
  const upserts: StudioWorkspaceVersionAutosavePayload['upserts'] = [];

  nextVersions.forEach((version) => {
    if (!version || typeof version.id !== 'string' || version.id.trim().length === 0) return;
    const versionId = version.id.trim();
    nextVersionIds.add(versionId);

    const existingDto = versionRegistry[versionId];
    const nextDto = toStudioWorkspaceVersionDtoFromRuntime(
      version,
      workspaceId,
      existingDto,
      projectId,
      nowIso
    );
    const nextPayload = toDtoAutosavePayload(nextDto);

    const previousVersion = previousVersionById.get(versionId);
    const previousPayload = previousVersion
      ? toDtoAutosavePayload(
          toStudioWorkspaceVersionDtoFromRuntime(
            previousVersion,
            workspaceId,
            existingDto,
            projectId,
            existingDto?.updatedAt ?? nowIso,
          )
        )
      : null;

    const hasChanged = !areAutosavePayloadsEqual(previousPayload, nextPayload);
    const hasDirtyAnnotationMask = version.annotationMaskIsDirty === true;
    const dirtyStateJustChanged =
      !previousVersion ||
      previousVersion.annotationMaskIsDirty !== true ||
      previousVersion.annotationMask !== version.annotationMask;
    const shouldCreate = !existingDto;
    if (!shouldCreate && !hasChanged && !(hasDirtyAnnotationMask && dirtyStateJustChanged)) {
      return;
    }

    upserts.push({
      id: versionId,
      action: shouldCreate ? 'CREATE' : 'UPDATE',
      payload: nextPayload,
      annotationMaskIsDirty: hasDirtyAnnotationMask,
    });
  });

  const detachIds: string[] = [];
  previousVersionById.forEach((_version, versionId) => {
    if (nextVersionIds.has(versionId)) return;
    const existingDto = versionRegistry[versionId];
    if (!existingDto) return;
    if (existingDto.workspaceId !== workspaceId) return;
    detachIds.push(versionId);
  });

  if (upserts.length === 0 && detachIds.length === 0) {
    return null;
  }

  return {
    upserts,
    detachIds,
  };
};
